/* 365Canvas blog behaviour: topic chips + "Load more" on the Guides index, table of contents,
   scroll spy, sticky bar and copy-link on articles. */

const PAGE = 12;
// Liquid's t filter HTML-escapes ' " & < >; the strings here go to textContent / template literals, so undo it
const D = (s) => (typeof s === 'string' ? s.replace(/&(#39|#34|quot|amp|lt|gt);/g, (m, e) => ({ '#39': "'", '#34': '"', quot: '"', amp: '&', lt: '<', gt: '>' })[e]) : s);
const T = (k, f) => D(window.C365_T && window.C365_T[k]) || f;

class C365Blog extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-grid]');
    this.cards = [...this.querySelectorAll('[data-card]')];
    this.promo = this.querySelector('[data-promo]');
    this.feat = this.querySelector('[data-feat]');
    this.more = this.querySelector('[data-more]');
    this.empty = this.querySelector('[data-empty]');
    this.total = this.querySelector('[data-total]');
    this.shown = this.querySelector('[data-shown]');
    this.bar = this.querySelector('[data-bar]');
    this.loadBtn = this.querySelector('[data-load]');
    this.tag = this.dataset.tag || '';
    this.limit = PAGE;
    // Chips filter in place when every guide is on the page (the untagged index); tagged URLs still work without JS.
    if (this.dataset.all === 'true') {
      this.querySelectorAll('[data-chip]').forEach((chip) => chip.addEventListener('click', (e) => {
        e.preventDefault();
        this.tag = chip.dataset.chip;
        this.limit = PAGE;
        this.querySelectorAll('[data-chip]').forEach((c) => {
          const on = c === chip;
          c.classList.toggle('is-active', on);
          c.setAttribute('aria-pressed', on);
        });
        history.replaceState(null, '', chip.href);
        this.render();
      }));
    }
    this.loadBtn?.addEventListener('click', () => { this.limit += PAGE; this.render(); });
    this.render();
  }

  render() {
    const featOn = !this.tag && this.feat;
    if (this.feat) this.feat.hidden = !featOn;
    const pool = this.cards.filter((c) => {
      if (featOn && c.dataset.handle === this.feat.dataset.handle) return false;
      return !this.tag || (c.dataset.tags || '').split('|').includes(this.tag);
    });
    this.cards.forEach((c) => { c.hidden = true; });
    pool.slice(0, this.limit).forEach((c) => { c.hidden = false; });
    const visible = pool.slice(0, this.limit);
    // Promo band sits after the 6th visible guide.
    if (this.promo) {
      this.promo.hidden = visible.length < 6;
      if (visible.length >= 6) visible[5].after(this.promo);
    }
    const total = pool.length + (featOn ? 1 : 0);
    const shown = visible.length + (featOn ? 1 : 0);
    if (this.empty) this.empty.hidden = total > 0;
    if (this.more) this.more.hidden = total === 0;
    if (this.grid) this.grid.hidden = pool.length === 0;
    if (this.total) this.total.textContent = total;
    if (this.shown) this.shown.textContent = shown;
    if (this.bar) this.bar.style.width = total ? `${Math.round((shown / total) * 100)}%` : '0%';
    if (this.loadBtn) this.loadBtn.hidden = shown >= total;
  }
}
customElements.define('c365-blog', C365Blog);

const EMOJI = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}️]/gu;

class C365Article extends HTMLElement {
  connectedCallback() {
    this.heads = [...this.querySelectorAll('.c365-rte h2')].filter((h) => h.textContent.trim());
    const used = new Set();
    const items = this.heads.map((h) => {
      const text = h.textContent.replace(EMOJI, '').replace(/\s+/g, ' ').trim();
      let id = h.id || text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'section';
      while (used.has(id)) id += '-2';
      used.add(id);
      h.id = id;
      return { text, id };
    });
    this.links = [];
    this.querySelectorAll('[data-toc]').forEach((list) => {
      items.forEach(({ text, id }) => {
        const a = document.createElement('a');
        a.href = `#${id}`;
        a.textContent = text;
        a.addEventListener('click', (e) => { e.preventDefault(); this.go(id); });
        this.links.push(a);
        if (list.tagName === 'OL') {
          const li = document.createElement('li');
          li.append(a);
          list.append(li);
        } else list.append(a);
      });
    });
    this.querySelectorAll('[data-toc-wrap]').forEach((w) => { w.hidden = items.length < 2; });

    this.tocBtn = this.querySelector('[data-toc-toggle]');
    this.tocList = this.querySelector('[data-toc-list]');
    this.tocBtn?.addEventListener('click', () => this.toggleToc());

    this.stickyBar = this.querySelector('[data-sticky]');
    this.querySelector('[data-copy]')?.addEventListener('click', (e) => this.copy(e.currentTarget));

    // Horizon scrolls inside .page-wrapper rather than the window, so listen in the capture phase for any scroller.
    this.onScroll = this.onScroll.bind(this);
    document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
    this.onScroll();
  }

  disconnectedCallback() { document.removeEventListener('scroll', this.onScroll, { capture: true }); }

  scroller() {
    const wrap = this.closest('.page-wrapper');
    return wrap && wrap.scrollHeight > wrap.clientHeight + 1 && getComputedStyle(wrap).overflowY !== 'visible' ? wrap : document.scrollingElement;
  }

  toggleToc(force) {
    const open = force ?? this.tocBtn.getAttribute('aria-expanded') !== 'true';
    this.tocBtn.setAttribute('aria-expanded', open);
    this.tocList.hidden = !open;
  }

  go(id) {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.replaceState(null, '', `#${id}`);
    if (this.tocBtn) this.toggleToc(false);
  }

  onScroll() {
    let active = 0;
    this.heads.forEach((h, i) => { if (h.getBoundingClientRect().top < 140) active = i; });
    const n = this.heads.length;
    this.links.forEach((a, i) => {
      const on = n > 0 && i % n === active;
      a.classList.toggle('is-current', on);
      if (on) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current');
    });
    if (this.stickyBar) {
      const sc = this.scroller();
      const shown = sc.scrollTop / Math.max(1, sc.scrollHeight - sc.clientHeight) > 0.3;
      this.stickyBar.classList.toggle('is-shown', shown);
      this.stickyBar.setAttribute('aria-hidden', !shown);
      this.stickyBar.querySelectorAll('a').forEach((a) => { a.tabIndex = shown ? 0 : -1; });
    }
  }

  async copy(btn) {
    const label = btn.querySelector('[data-copy-label]');
    try { await navigator.clipboard.writeText(btn.dataset.url || location.href); } catch (e) { /* clipboard blocked */ }
    label.textContent = T('blog.link_copied', 'Link copied');
    clearTimeout(this.copyTimer);
    this.copyTimer = setTimeout(() => { label.textContent = T('blog.copy_link', 'Copy link'); }, 2000);
  }
}
customElements.define('c365-article', C365Article);
